import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {GridColDef, GridSortModel} from '@mui/x-data-grid';
import {useTranslation} from 'react-i18next';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import ArrPagination from '../../common/arr-pagination/ArrPagination';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrCheckbox from '../../common/arr-checkbox/ArrCheckbox';
import {ReactComponent as ExportIcon} from '../../../assets/images/upload.svg';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import './IngestionErrors.scss';
import {
  usePostIngestionErrors,
  useUpdateIngestionErrors,
  useExportIngestionErrors,
  usePostIgnoreAllErrors,
  usePostIgnoreAllBtnErrors,
} from '../../../hooks/ingestions/useIngestions';
import {PAGE_SIZE, ToastType} from '../../../shared/constants';
import {
  ingestionDetailsModel,
  IngestionErrorsIgnoreAllPayload,
  IngestionErrorsIgnoreAllRequestPayload,
  IngestionErrorsModelPayload,
} from '../../../api/types/ingestionApi.type';

export interface IngestionError {
  id?: string;
  fileName?: string;
  rowNo?: string | number;
  error?: string;
  documentType?: string;
  subject?: string;
  parentID?: string;
  action?: boolean;
  canIgnore: boolean;
}
interface IngestionErrorsProps {
  showModal: boolean;
  ingestionName: string;
  onClose: (updatedRows?: IngestionError[]) => void;
  projectId: number;
  ingestionId: number;
  ingestionDetailsModel?: ingestionDetailsModel;
  ingestionState: string;
  name: string;
  onCloseModal: (toastDetails: {
    toastType: ToastType;
    toastMessage: string;
  }) => void;
  onDoneSuccess?: () => void;
}

const INGESTION_FAILED = 'FAILED';

const IngestionErrors: React.FC<IngestionErrorsProps> = ({
  showModal,
  onClose,
  ingestionName,
  projectId,
  ingestionDetailsModel,
  ingestionId,
  ingestionState,
  name,
  onCloseModal,
  onDoneSuccess,
}) => {
  const {t} = useTranslation();

  const [exportParams, setExportParams] = useState<any | null>(null);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [rows, setRows] = useState<IngestionError[]>([]);
  const [showIgnoreAllModal, setShowIgnoreAllModal] = useState<boolean>(false);
  const [isAllErrorsIgnored, setIsAllErrorsIgnored] = useState<boolean>(false);

  const {mutate: fetchIngestionErrors} = usePostIngestionErrors();
  const {mutate: updateIngestionErrors} = useUpdateIngestionErrors();
  const {refetch: exportApi} = useExportIngestionErrors(exportParams);
  const {mutate: ignoreAllErrors} = usePostIgnoreAllBtnErrors();
  const errorType = name.toUpperCase();
  const ignorePayload: IngestionErrorsIgnoreAllPayload = {
    projectId,
    ingestionId,
    ingestionName,
    ingestionStatus: ingestionDetailsModel?.status || '',
    errorType,
    docType: 'ALL',
  };

  const {data: ignoreAllResponseData} = usePostIgnoreAllErrors(ignorePayload);
  const ingestionStatus = ignoreAllResponseData?.ingestionStatus;
  const disableIgnoreAllFlag = ignoreAllResponseData?.disableIgnoreAll;
  const ignoreAllButtonDisabled =
    disableIgnoreAllFlag === 1 ||
    (disableIgnoreAllFlag === 0 &&
      ingestionStatus?.toUpperCase() !== INGESTION_FAILED);

  const PAGE_OPTIONS = [10, 25, 50];
  const setPageSizeHandler = (size: number) => {
    setPageSize(size);
    setPageNumber(1); // Reset to first page when page size changes
  };
  const setPageNumberHandler = (page: number) => {
    setPageNumber(page);
  };

  /** Sorting */
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'fileName', sort: 'asc'},
  ]);

  const getSortIngestions = (model: GridSortModel | undefined) => {
    const field = model?.[0]?.field ?? '';
    const rawDir =
      (model?.[0]?.sort as 'asc' | 'desc' | undefined) ?? undefined;

    return {
      sortby: field === 'fileName' ? 'FileName' : field,
      sortingOrder: rawDir ? rawDir.toUpperCase() : '',
      sortingColumn: field === 'fileName' ? 'FileName' : field,
    };
  };

  const getRowID = (row: any, index: number) => {
    if (row?.id) return String(row.id);
    return `${row?.fileName ?? 'row'}-${row?.rowNo ?? 'no'}-${index}`;
  };

  useEffect(() => {
    const payload: IngestionErrorsModelPayload = {
      ingestionId,
      ingestionState,
      docType: 'All',
      unPublishId: 0,
      errorType,
      pageNumber,
      pageSize,
      ...getSortIngestions(sortModel),
    };

    fetchIngestionErrors(payload, {
      onSuccess: (response: any) => {
        const data = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.items)
            ? response.data.items
            : Array.isArray(response)
              ? response
              : (response?.data ?? []);

        const mapped = data.map((item: any, idx: number) => ({
          ...item,
          id: getRowID(item, idx),
          action: !!item?.action,
        }));
        setRows(mapped);
        setTotalRecords(response?.totalRecords || 0);
      },
      onError: (err: any) => {
        console.error('Failed to fetch ingestion errors:', err);
      },
    });
  }, [ingestionId, ingestionState, pageNumber, pageSize, sortModel]);

  /** Handle checkbox change */
  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    setRows((prev) =>
      prev.map((r) =>
        String(r.id) === String(rowId) ? {...r, action: checked} : r
      )
    );
  };

  const handleToggleAll = (checked: boolean) => {
    setRows((prev: IngestionError[]) =>
      prev.map((r) => ({...r, action: checked}))
    );
  };

  /** ingestion Error cols */
  const ingestionErrorCols: GridColDef[] = [
    {
      field: 'fileName',
      headerName: t('ingestions.mapped.ingestionErrors.sourceDocId'),
      flex: 1,
    },
    {
      field: 'rowNo',
      headerName: t('ingestions.mapped.ingestionErrors.rowNum'),
      flex: 0.7,
    },
    {
      field: 'error',
      headerName: t('ingestions.mapped.ingestionErrors.error'),
      flex: 2,
    },
    {
      field: 'documentType',
      headerName: t('ingestions.mapped.ingestionErrors.fileName'),
      flex: 1,
    },
    {
      field: 'subject',
      headerName: t('ingestions.mapped.ingestionErrors.docFileType'),
      flex: 1,
    },
    {
      field: 'parentID',
      headerName: t('ingestions.mapped.ingestionErrors.parentDocId'),
      flex: 1,
    },
    {
      field: 'action',
      headerName: t('ingestions.mapped.ingestionErrors.action'),
      flex: 1,
      sortable: false,
      headerClassName: 'last-column',
      renderCell: (params) => (
        <ArrCheckbox
          className="ingestion-checkbox"
          disabled={params.row?.canIgnore === false}
          checked={!!params.row?.action}
          size="large"
          labelText="Ignore"
          onChange={(checked) =>
            handleCheckboxChange(String(params.row.id), checked)
          }
          aria-label={`action-${params.id}-cb`}
        />
      ),
    },
  ];

  const handleErrorModalClose = () => {
    if (isAllErrorsIgnored) {
      onDoneSuccess?.();
    }
    onClose();
  };

  /** Update API */
  const handleUpdate = () => {
    if (isAllErrorsIgnored) {
      onDoneSuccess?.();
      setIsAllErrorsIgnored(false);
      onClose();
      onCloseModal({
        toastType: ToastType.Success,
        toastMessage: t('ingestions.mapped.ingestionErrors.updateSuccess'),
      });
      return;
    }
    const errorArray = rows.map((row) => ({
      clientUniqueCode: row.fileName ?? '',
      action: row.action ? 'Ignored' : 'unselected',
    }));
    updateIngestionErrors(
      {
        errorType,
        ingestionId,
        projectId,
        errorArray,
      },
      {
        onSuccess: () => {
          onDoneSuccess?.();
          onClose();
          onCloseModal({
            toastType: ToastType.Success,
            toastMessage: t('ingestions.mapped.ingestionErrors.updateSuccess'),
          });
        },
        onError: () => {
          onClose();
          onCloseModal({
            toastType: ToastType.Error,
            toastMessage: t('ingestions.mapped.ingestionErrors.updateFailed'),
          });
        },
      }
    );
  };

  //Export API
  const handleExport = async () => {
    const params = {
      ProjectId: projectId,
      IngestionId: ingestionId,
      DocType: 'ALL',
      ErrorType: errorType,
    };
    setExportParams(params);
    try {
      await exportApi();
      onClose();
      onCloseModal({
        toastType: ToastType.Success,
        toastMessage: t('ingestions.mapped.ingestionErrors.exportSuccess'),
      });
    } catch (err: any) {
      onClose();
      onCloseModal({
        toastType: ToastType.Error,
        toastMessage: t('ingestions.mapped.ingestionErrors.exportFailed'),
      });
      console.error(err);
    }
  };

  // Ignore All API
  const handleIgnoreAll = () => {
    setShowIgnoreAllModal(false);
    handleToggleAll(true);
    const ignoreAllPayload: IngestionErrorsIgnoreAllRequestPayload = {
      projectId: Number(ignorePayload.projectId),
      ingestionId: Number(ignorePayload.ingestionId),
      errorType: String(ignorePayload.errorType),
      isIgnore: true,
    };
    if (!ignoreAllPayload.projectId || !ignoreAllPayload.ingestionId) {
      onCloseModal({
        toastType: ToastType.Warning,
        toastMessage:
          t('ingestions.mapped.ingestionErrors.missingRequiredIdentifiers') ||
          'Missing project or ingestion identifier',
      });
      return;
    }

    ignoreAllErrors(ignoreAllPayload, {
      onSuccess: () => {
        setIsAllErrorsIgnored(true);
      },
      onError: () => {
        setShowIgnoreAllModal(false);
        onCloseModal({
          toastType: ToastType.Error,
          toastMessage: t('ingestions.mapped.ingestionErrors.ignoreFailed'),
        });
      },
    });
  };
  // Modal heading
  const modalHeading = ingestionName?.trim()?.length
    ? `${t('ingestions.mapped.ingestionErrors.ingestionName')} ${ingestionName}`
    : t('ingestions.mapped.ingestionErrors.ingestionName');

  return (
    <>
      <ArrModal
        showModal={showModal}
        onClose={handleErrorModalClose}
        dataTestId="ingestion-errors-modal"
        headerTitleElement={
          <Box display="flex" alignItems="center" className="title-container">
            <Typography
              className="typo-text headline3_size heading-title-text"
              tabIndex={0}
              data-testid={`ingestion-errors-title`}
            >
              {t('ingestions.mapped.ingestionErrors.title')}
            </Typography>
            <ArrTooltipWrapper
              title={t(
                'ingestions.mapped.ingestionErrors.infoIngestions.title'
              )}
              trigger={
                <Box display="flex">
                  <InfoIcon className="error-list-info-icon" tabIndex={0} />
                </Box>
              }
            >
              {t('ingestions.mapped.ingestionErrors.infoIngestions.overview')}
            </ArrTooltipWrapper>
          </Box>
        }
        autoFocusCloseButton
        className="ingestion-errors-modal"
      >
        <Box className="ingestion-errors-root">
          <Typography
            className="typo-text body2_size desc-text-color"
            data-testid={`modal-heading`}
            tabIndex={0}
          >
            {modalHeading}
          </Typography>
          <Box className="ingestion-errors-container">
            <ArrDataGrid
              rows={rows ?? []}
              columns={ingestionErrorCols}
              getRowId={(row: IngestionError) =>
                String(row?.id ?? getRowID(row))
              }
              paginationMode="server"
              rowCount={totalRecords}
              paginationModel={{
                page: pageNumber - 1,
                pageSize: pageSize,
              }}
              onPaginationModelChange={(model) => {
                setPageNumberHandler(model.page + 1);
                setPageSizeHandler(model.pageSize);
              }}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={(m) => {
                setSortModel(m);
                setPageNumberHandler(1);
              }}
              style={{height: '100%'}}
              className="ingestion-errors-grid"
              hideFooterPagination={true}
              showCellVerticalBorder={true}
            />

            <Box className="ingestion-errors-footer">
              <ArrPagination
                totalItems={totalRecords}
                currentPage={pageNumber}
                perPage={pageSize}
                onPageChange={setPageNumberHandler}
                onPerPageChange={setPageSizeHandler}
                showPerPageOptions
                perPageOptions={PAGE_OPTIONS}
                entityType="Errors"
                showInfoText={false}
                alwaysShow
              />

              <Box className="ingestion-errors-buttons">
                <ArrButton
                  variant="secondary"
                  size="medium"
                  color="inherit"
                  disabled={ignoreAllButtonDisabled}
                  onClick={
                    ignoreAllResponseData?.disableIgnoreAll === 1
                      ? undefined
                      : () => setShowIgnoreAllModal(true)
                  }
                  dataTestId="ingestion-errors-ignore-all-button"
                >
                  {t('ingestions.ignoreAll')}
                </ArrButton>

                <ArrButton
                  startIcon={<ExportIcon className="upload-icon" />}
                  variant="primary"
                  size="medium"
                  className="export-btn"
                  onClick={handleExport}
                  dataTestId="ingestion-errors-export-button"
                >
                  {t('ingestions.export')}
                </ArrButton>

                <ArrButton
                  variant="primary"
                  size="medium"
                  disabled={
                    rows.every((row) => row.canIgnore === false) ||
                    ingestionState?.toUpperCase() !== INGESTION_FAILED
                  }
                  onClick={handleUpdate}
                  dataTestId="ingestion-errors-done-button"
                >
                  {t('ingestions.done')}
                </ArrButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </ArrModal>
      <ArrModal
        showModal={showIgnoreAllModal}
        title={t('ingestions.mapped.ingestionErrors.ignoreAllModalHeading')}
        description={t(
          'ingestions.mapped.ingestionErrors.ignoreAllModalDescription'
        )}
        onClose={() => setShowIgnoreAllModal(false)}
        autoFocusCloseButton={true}
        dataTestId="ignore-all-modal"
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.cancel'),
            dataTestId: 'iam-cancel-button',
            onClick: () => setShowIgnoreAllModal(false),
            variant: 'secondary',
          },
          {
            id: 'delete-button',
            label: t('common.ok'),
            dataTestId: 'iam-ok-button',
            onClick: handleIgnoreAll,
            variant: 'primary',
          },
        ]}
      />
    </>
  );
};

export default IngestionErrors;
